import moment from "moment";
import { useEffect, useState } from "react";

import { getNewUsersByTimePeriod } from "../../lib/queries";

const defaultTimeFormat = "MM/DD/YY";

type Day = {
  day: string;
};
type Week = {
  week: string;
};
type Month = {
  month: string;
};
type Total = {
  total: string;
};

export type NewMembersDay = Day & Total;
export type NewMembersWeek = Week & Total;
export type NewMembersMonth = Month & Total;

/**
 * Loads new members grouped by day, week and month for the given team or guild
 * @param startTime the start time
 * @param endTime the end time
 * @param teamId the team id for Slack; the guild id for Discord
 * @param platform "slack" or "discord"
 * @returns
 * newMembersDay: total members grouped by day data
 * newMembersWeek: total members grouped by week data
 * newMembersMonth: total members grouped by month data
 * loading: wether the hook is still loading
 */
function useNewMembers(
  startTime: string,
  endTime: string,
  teamId: string,
  platform: string
) {
  const [loading, setLoading] = useState<boolean>(true);
  const [newMembersDay, setNewMembersDay] = useState<NewMembersDay[]>([]);
  const [newMembersWeek, setNewMembersWeek] = useState<NewMembersWeek[]>([]);
  const [newMembersMonth, setNewMembersMonth] = useState<NewMembersMonth[]>([]);

  /**
   * Loads new members grouped by day, week and month
   */
  const loadNewMembers = async () => {
    setLoading(true);
    let params;

    if (platform === "slack") {
      params = {
        teamId,
        startTime: moment(startTime).format(defaultTimeFormat),
        endTime: moment(endTime).format(defaultTimeFormat),
      };
      const byDay: NewMembersDay[] = await getNewUsersByTimePeriod(
        params.teamId,
        params.startTime,
        params.endTime,
        "day"
      );
      const byWeek: NewMembersWeek[] = await getNewUsersByTimePeriod(
        params.teamId,
        params.startTime,
        params.endTime,
        "week"
      );
      const byMonth: NewMembersMonth[] = await getNewUsersByTimePeriod(
        params.teamId,
        params.startTime,
        params.endTime,
        "month"
      );
      setNewMembersDay(byDay);
      setNewMembersWeek(byWeek);
      setNewMembersMonth(byMonth);
      setLoading(false);
    } else {
      // <--- TODO: Add Discord queries
      setNewMembersDay([]);
      setNewMembersWeek([]);
      setNewMembersMonth([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNewMembers();
  }, [startTime, endTime, platform]);

  return {
    newMembersDay,
    newMembersWeek,
    newMembersMonth,
    loading,
  };
}

export default useNewMembers;
